import { Link } from 'react-router';
import React, { useRef } from 'react';
import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import {
    useAdEventHandler,
    useLogImpression,
    useTracker,
} from '@archipro-website/tracker';
import type { AdvertCreative } from '@modules-rocco/ads/types';
import { createLinkedComponentParams } from '~/utils/urlHelper';

interface DirectoryAdTileProps {
    advertCreative: AdvertCreative;
}

/**
 * Tall ad tile for the directory product grid.
 * Designed to span 2 rows visually with "FEATURED" badge.
 * Uses CategoryPlacement carousel images for the ad creative.
 *
 * Pattern references:
 * - Layout/height: DirectoryFeaturedTile.tsx
 * - Tracking: AdvertBannerTile.tsx
 */
export const DirectoryAdTile = React.memo(
    ({ advertCreative }: DirectoryAdTileProps) => {
        const id = advertCreative.id;
        const title = advertCreative.title;
        const link = advertCreative.cta.href;
        const professionalID = advertCreative.professional.id;
        const professionalName = advertCreative.cta.label;

        // Use carousel desktop image as the cover (CategoryPlacement provides carousel, not video)
        const coverImage =
            advertCreative.carousel?.[0]?.image?.desktop ||
            advertCreative.video?.cover ||
            '';

        const tileRef = useRef<HTMLAnchorElement>(null);

        // Ad impression tracking
        const tracker = useTracker();
        useLogImpression({
            data: {
                professionalID: Number(professionalID),
                itemID: Number(id),
                type: 'VerticalTile_Advert',
            },
            ref: tileRef,
        });

        // Ad click tracking
        const { onClick } = useAdEventHandler(advertCreative, tileRef);
        const { onClick: trackClick } = createLinkedComponentParams(
            tracker,
            'Ad_ImageClick',
            advertCreative.cta.href,
            {
                libraryItemID: advertCreative.professional.id,
                professionalID: advertCreative.professional.id,
                professionalTitle: advertCreative.professional.title,
            },
            null,
            onClick,
        );

        return (
            <Link
                to={link}
                ref={tileRef}
                prefetch="intent"
                onClick={trackClick}
                className={cn(
                    'group relative flex flex-col gap-[0.9375rem]',
                    'transition-opacity duration-150',
                )}
            >
                {/* Image container with intrinsic height via padding-bottom */}
                {/* Height = 2 x width (matching 2 aspect-square tiles) + 9rem gap */}
                <span
                    className={cn(
                        'relative block w-full overflow-hidden rounded-lg',
                        'bg-[#FAFAFA]',
                    )}
                >
                    {/* Spacer creates intrinsic height based on width percentage */}
                    <span
                        className="block"
                        style={{ paddingBottom: 'calc(200% + 9rem)' }}
                    />
                    <span className="absolute inset-0 overflow-hidden">
                        {coverImage && (
                            <Image
                                src={coverImage}
                                alt={title || ''}
                                fill
                                fit="cover"
                                sizes="(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw"
                                className="transition-transform duration-300 group-hover:scale-105"
                            />
                        )}

                        {/* Featured badge - top-left */}
                        <span className="absolute left-5 top-5 flex h-[1.625rem] items-center justify-center rounded-full bg-[#f2f2f2] px-4">
                            <span className="text-[0.625rem] font-medium uppercase leading-[0.625rem] tracking-[0.05rem] text-[#141414]">
                                featured
                            </span>
                        </span>
                    </span>
                </span>

                {/* Ad info */}
                <span className="flex shrink-0 flex-col gap-0.5">
                    <span className="line-clamp-1 text-lg font-medium leading-[1.3] tracking-[-0.0225rem] text-[#141414]">
                        {title}
                    </span>
                    <span className="line-clamp-1 text-base font-medium leading-[1.3] tracking-[-0.02rem] text-[#737373]">
                        {professionalName}
                    </span>
                </span>
            </Link>
        );
    },
);

DirectoryAdTile.displayName = 'DirectoryAdTile';
